import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Button, Card, Col, Divider, Modal, Row, Table } from 'antd';
import { imageUrl, noImageUrl } from '../../config/config';

import ProductDetail from './ProductDetail';
import ProductPriceImport from './ProductPriceImport';
import ProductsQuery from '../../components/Query/Products';
import PriceDisplay from '../../components/Others/PriceDisplay';
import { getPrivilegesTo } from 'src/helpers/Users/getPrivilegesTo';
import { canImportPrices, privileges } from 'data-layer/src/services/Users/privileges';
import AuthService from '../../services/AuthService';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

interface IProductsTableProps extends RouteComponentProps {
  domain: string;
  searchQuery: string;
  currency: GQL.ICurrency;
}

interface IProductsTableState {
  productId: string | undefined;
  priceImportVisible: boolean;
  page: number;
  limit: number;
}

class ProductsTable extends React.Component<IProductsTableProps, IProductsTableState> {

  constructor(props: any) {
    super(props);

    this.state = {
      limit: 10,
      page: 1,
      productId: undefined,
      priceImportVisible: false,
    };
  }

  public render() {
    return (
      <Row>
        <Col span={24}>
          <ProductsQuery
            search={this.props.searchQuery}
            limit={this.state.limit}
            skip={(this.state.page - 1) * this.state.limit}
          >
            {({ products, deleteProduct }: any) => this.renderData(products, deleteProduct)}
          </ProductsQuery>
        </Col>
      </Row>
    );
  }

  private renderData(products: any, deleteProduct: Function) {
    // primární měna
    const { currency } = this.props;

    const privilegesNewProducts = getPrivilegesTo(privileges.ADDING_NEW_PRODUCTS);
    const privilegesEditProducts = getPrivilegesTo(privileges.EDITING_PRODUCTS);

    if (products && products.products) {
      return (
        <div>
          <Divider />
          <Button
            disabled={!privilegesNewProducts}
            onClick={() => this.newProduct()}
          >
            Create new product
          </Button>
          {canImportPrices(AuthService.GetInstance().loggedUser?.email) &&
            <Button style={{ marginLeft: 8 }} onClick={() => this.setState({ priceImportVisible: true })}>
              Import prices
            </Button>}
          <Table
            rowKey={(record: GQL.IProduct) => record.id}
            dataSource={products.products.edges.map((e: GQL.IProductEdge) => e.node)}
            pagination={{
              current: this.state.page,
              showTotal: () => true,
              total: products.products.aggregate.count,
              // showSizeChanger: true,
              onChange: page => this.setState({ page })
            }}
            defaultExpandAllRows={true}
            style={{ marginTop: '20px' }}
          >
            <Table.Column
              title="Product"
              dataIndex="title"
              key="title"
              render={(u, product: GQL.IProduct) => {
                // todo: ze serveru nepřijdou obrázky setříděné ...
                const image = product.images && product.images.length > 0 
                  ? product.images.reduce((output: GQL.IImage | null, image: GQL.IImage) => {
                    if (output === null) {
                      output = image;
                    }
                    if (image.priority < output.priority) {
                      output = image;
                    }
                    return output;
                  }, null) : null;
                
                return (
                  <table>
                    <tbody>
                      <tr>
                        {image &&
                          <td style={{ width: '70px', textAlign: 'center' }}>
                            <img
                              style={{ float: 'left', width: '50px', maxHeight: '50px' }}
                              alt={product.name['en'] || product.title}
                              src={(image && `${imageUrl}/static/${image.file}`) || noImageUrl}
                            />
                          </td>
                        }
                        <td>
                          <strong style={{ height: '100%' }}>{product.name['en'] || product.title}</strong>
                          {product.variants && product.variants.length > 1 &&
                            <React.Fragment>
                              <br />
                              <span style={{ color: '#bfbfbf' }}>{product.variants.length} variants</span>
                            </React.Fragment>}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                );
              }}
            />
            <Table.Column
              title="Price"
              dataIndex="price"
              key="price"
              align="right"
              render={(u, row: GQL.IProduct) => {
                if (!currency) {
                  return null;
                }

                if(row.variants?.length === 0) {
                  return null;
                }

                const firstVariantPrices = row.variants && row.variants[0] && row.variants[0].prices;

                const prices = firstVariantPrices
                  ? firstVariantPrices.map((price: any) => {
                      return {
                        currencyId: price.currencyId,
                        priceWithout: price.priceWithout,
                      };
                    }) 
                  : [];

                return (<PriceDisplay prices={prices} currency={currency} />);
              }}
            />
            <Table.Column
              title="Manufacturer"
              dataIndex="manufacturer.name"
              key="manufacturer.name"
              width={150}
            />
            <Table.Column
              title="In catalog"
              dataIndex="manufacturer.display"
              key="manufacturer.display"
              width={100}
              align="center"
              render={(u, row: GQL.IProduct) => {
                return (
                  <span>
                    {row.display 
                      ? <CheckOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                      : <CloseOutlined style={{ fontSize: 20 }} />}
                  </span>
                );
              }}
            />
            <Table.Column
              width={180}
              title="Actions"
              dataIndex="actions"
              key="actions"
              align="center"
              render={(u: any, row: any) => this.renderActions(u, row, deleteProduct, products.refetch)}
            />
          </Table>

          {this.state && this.state.productId 
            && ((privilegesNewProducts && this.state.productId === '-1')
              || (privilegesEditProducts && this.state.productId !== '-1')) &&
            <ProductDetail
              domain={this.props.domain}
              productId={this.state.productId}
              onCancel={() => this.setCurrentProduct(undefined)}
              onSave={() => {
                products.refetch(); 
                this.setCurrentProduct(undefined);
              }}
            />}

          {this.state.priceImportVisible &&
            <ProductPriceImport
              onClose={() => this.setState({ priceImportVisible: false })}
              onImported={() => {
                products.refetch();
                this.setState({ priceImportVisible: false });
              }}
            />}
        </div>
      );
    }

    return (
      <div>
        <Divider />
        <Card loading={true} title="Loading products ..." style={{ width: '100%' }} />
      </div>
    );
  }

  private renderActions(u: any, product: any, deleteCategory: Function, refetch: Function) {
    const privilegesEditProducts = getPrivilegesTo(privileges.EDITING_PRODUCTS);
    const privilegesDeleteProducts = getPrivilegesTo(privileges.DELETING_PRODUCTS);
    
    return (
      <div>
        <Button type={'dashed'} disabled={!privilegesEditProducts} onClick={() => this.setCurrentProduct(product.id)}>
          Edit
        </Button>
        <Divider type={'vertical'} />
        <Button danger={true} disabled={!privilegesDeleteProducts} onClick={() => this.delete(product, deleteCategory, refetch)}>
          Delete
        </Button>
      </div>
    );
  }

  private newProduct() {
    this.setCurrentProduct('-1');
  }

  private setCurrentProduct(productId: string | undefined) {
    this.setState({
      productId
    });
  }

  private delete(product: any, deleteProduct: Function, refetch: Function) {
    if (getPrivilegesTo(privileges.DELETING_PRODUCTS)) {
      Modal.confirm({
        title: `Delete ${product.title}?`,
        content: (
          <span>
            Are you sure you want to delete product <strong>{product.title}</strong>
          </span>
        ),
        onOk: () => {
          return deleteProduct({
            variables: {
              id: product.id
            }
          }).then(() => refetch());
        }
      });
    }
  }
}

export default withRouter(ProductsTable);


// WEBPACK FOOTER //
// ./src/scenes/Products/ProductsTable.tsx